// SHADOW NETWORK — Posts feed.
// Fetches /shadow-posts (public) and renders <AnalysisCard>s in a grid.
// Mounted from ShadowNetworkPage as a sibling block — no existing markup touched.
import React, { useCallback, useEffect, useState } from "react";
import { Filter, Loader2 } from "lucide-react";
import { API, safeJson } from "../lib/api";
import { AnalysisCard } from "./AnalysisCard";
import { PublishAnalysisModal } from "./PublishAnalysisModal";
import { useLanguage } from "../i18n/LanguageContext";
import { getShadowI18n } from "../i18n/shadow";

const TIMEFRAMES = ["", "1M", "5M", "15M", "1H", "4H", "1D", "1W"];
const DIRECTIONS = ["", "LONG", "SHORT", "NEUTRAL"];

// Merge two analysis arrays keeping the first occurrence of each id.
// Prevents React duplicate-key warnings when pagination, repost or a new
// publish overlap with items already in the local list.
const _dedupeById = (arr) => {
  const seen = new Set();
  const out = [];
  for (const item of arr) {
    const k = item?.id;
    if (k == null || seen.has(k)) continue;
    seen.add(k);
    out.push(item);
  }
  return out;
};

export const ShadowFeed = () => {
  const { lang } = useLanguage();
  const ui = getShadowI18n(lang);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ ticker: "", direction: "", timeframe: "" });

  const load = useCallback(async (p = 1, append = false) => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ page: String(p), limit: "12" });
      if (filters.ticker) qs.set("ticker", filters.ticker.toUpperCase());
      if (filters.direction) qs.set("direction", filters.direction);
      if (filters.timeframe) qs.set("timeframe", filters.timeframe);
      const res = await fetch(`${API}/shadow-posts?${qs.toString()}`, {
        credentials: "include",
      });
      const data = await safeJson(res);
      if (res.ok && data) {
        setItems((prev) =>
          _dedupeById(append ? [...prev, ...data.analyses] : data.analyses)
        );
        setHasMore(!!data.has_more);
        setPage(p);
      }
    } finally {
      setLoading(false);
    }
  }, [filters.ticker, filters.direction, filters.timeframe]);

  // Initial + filter changes
  useEffect(() => {
    load(1, false);
  }, [load]);

  const onPublished = (analysis) => {
    if (!analysis) return;
    setItems((prev) => _dedupeById([analysis, ...prev]));
  };

  const onDeleted = (id) => {
    setItems((prev) => prev.filter((p) => p.id !== id));
  };

  return (
    <section className="px-4 py-12 md:px-8 lg:px-12" data-testid="shadow-feed-section">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-orange-300/80">
              {ui.feed_eyebrow}
            </p>
            <h2 className="mt-1 font-display text-3xl font-black tracking-tight md:text-4xl">
              {ui.feed_title}
            </h2>
            <p className="mt-1 max-w-xl text-sm text-white/45">
              {ui.feed_sub}
            </p>
          </div>
          <PublishAnalysisModal onPublished={onPublished} />
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-white/35" />
          <input
            value={filters.ticker}
            onChange={(e) => setFilters((f) => ({ ...f, ticker: e.target.value.toUpperCase().slice(0, 16) }))}
            placeholder="BTC/USDT"
            className="w-28 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] uppercase tracking-wider outline-none placeholder:text-white/30 focus:border-orange-400/40"
            data-testid="shadow-feed-filter-ticker"
          />
          <Select
            value={filters.timeframe}
            onChange={(v) => setFilters((f) => ({ ...f, timeframe: v }))}
            options={TIMEFRAMES}
            placeholder={ui.feed_filter_tf_ph}
            testid="shadow-feed-filter-timeframe"
          />
          <Select
            value={filters.direction}
            onChange={(v) => setFilters((f) => ({ ...f, direction: v }))}
            options={DIRECTIONS}
            placeholder={ui.feed_filter_dir_ph}
            testid="shadow-feed-filter-direction"
          />
          {(filters.ticker || filters.timeframe || filters.direction) && (
            <button
              onClick={() => setFilters({ ticker: "", direction: "", timeframe: "" })}
              className="rounded-full border border-white/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-white/45 hover:border-white/30 hover:text-white"
              data-testid="shadow-feed-filter-clear"
            >
              {ui.feed_clear}
            </button>
          )}
        </div>

        {/* Grid */}
        {loading && items.length === 0 ? (
          <div className="mt-12 flex items-center justify-center text-white/45">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> {ui.feed_loading}
          </div>
        ) : items.length === 0 ? (
          <div className="mt-12 rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center">
            <p className="text-sm text-white/55">
              {ui.feed_empty_title}
            </p>
            <p className="mt-1 text-xs text-white/35">
              {ui.feed_empty_sub}
            </p>
          </div>
        ) : (
          <>
            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {items.map((p) => (
                <AnalysisCard
                  key={p.id}
                  analysis={p}
                  onDeleted={onDeleted}
                  onUpdated={(u) => setItems((prev) => prev.map((x) => (x.id === u.id ? u : x)))}
                />
              ))}
            </div>
            {hasMore && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => load(page + 1, true)}
                  disabled={loading}
                  className="rounded-full border border-white/15 bg-white/[0.04] px-6 py-2.5 text-[11px] font-black uppercase tracking-[0.2em] text-white/80 hover:border-orange-400/50 hover:text-orange-200 disabled:opacity-60"
                  data-testid="shadow-feed-loadmore"
                >
                  {loading ? ui.feed_loadmore_loading : ui.feed_loadmore}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

const Select = ({ value, onChange, options, placeholder, testid }) => (
  <div className="relative">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none rounded-full border border-white/10 bg-white/[0.04] py-1.5 pl-3 pr-7 text-[11px] uppercase tracking-wider outline-none focus:border-orange-400/40"
      data-testid={testid}
    >
      {options.map((o) => (
        <option key={o || "_"} value={o}>{o || placeholder}</option>
      ))}
    </select>
  </div>
);

export default ShadowFeed;
